import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Package, Plus, Search, Eye, PenSquare, Tag, Trash2, Download, RefreshCw, CheckCircle2, Clock, X, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CategoryModal from '../components/categories/CategoryModal';
import DeleteProductModal from '../components/products/DeleteProductModal';
import WooCommerceProductImport from '../components/integrations/WooCommerceProductImport';
import ImportFromStoreButton from '../components/integrations/ImportFromStoreButton';
import BulkUpdateModal from '../components/products/BulkUpdateModal';
import { toast } from 'react-toastify';

interface Product {
  id: string;
  name: string;
  sku: string;
  ean: string;
  baselinker_id?: string;
  regularPrice: number;
  salePrice?: number;
  promotionalPeriod?: {
    startDate: string;
    endDate: string;
  };
  stockQuantity: number;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  mainImage?: string;
  companyId: string;
  createdAt: string;
  lastUpdate?: string;
  woocommerce?: {
    synced: boolean;
  };
  synced: boolean;
  isProPlus: boolean;
  proPlusPrice: number;
  status?: 'pending' | 'approved';
  category?: string;
  inPricebench?: boolean;
}

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || '');
  const [showPendingProducts, setShowPendingProducts] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser?.companyId) return;

    setLoading(true);

    // Criar queries
    const approvedProductsRef = collection(db, 'products');
    const approvedQuery = query(
      approvedProductsRef,
      where('companyId', '==', currentUser.companyId),
      orderBy('createdAt', 'desc')
    );

    const pendingProductsRef = collection(db, 'pendingProducts');
    const pendingQuery = query(
      pendingProductsRef,
      where('companyId', '==', currentUser.companyId),
      orderBy('createdAt', 'desc')
    );

    const pricebenchRef = collection(db, 'pricebench');
    const pricebenchQuery = query(
      pricebenchRef,
      where('companyId', '==', currentUser.companyId)
    );

    // Configurar listeners
    const unsubApproved = onSnapshot(approvedQuery, approvedSnapshot => {
      const approvedProducts = approvedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        status: 'approved'
      }));

      // Atualizar produtos aprovados sem recarregar toda a lista
      setProducts(prevProducts => {
        const pendingProducts = prevProducts.filter(p => p.status === 'pending');
        const newProducts = [...approvedProducts, ...pendingProducts];
        setLoading(false);
        return newProducts;
      });
    });

    const unsubPending = onSnapshot(pendingQuery, pendingSnapshot => {
      const pendingProducts = pendingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        status: 'pending'
      }));

      // Atualizar produtos pendentes sem recarregar toda a lista
      setProducts(prevProducts => {
        const approvedProducts = prevProducts.filter(p => p.status === 'approved');
        const newProducts = [...approvedProducts, ...pendingProducts];
        setLoading(false);
        return newProducts;
      });
    });

    const unsubPricebench = onSnapshot(pricebenchQuery, pricebenchSnapshot => {
      const pricebenchProductIds = new Set(
        pricebenchSnapshot.docs.map(doc => doc.data().productId)
      );

      // Atualizar status do pricebench sem recarregar toda a lista
      setProducts(prevProducts => 
        prevProducts.map(product => ({
          ...product,
          inPricebench: pricebenchProductIds.has(product.id)
        }))
      );
    });

    // Cleanup function
    return () => {
      unsubApproved();
      unsubPending();
      unsubPricebench();
    };
  }, [currentUser?.companyId]); // Só recriar os listeners quando mudar a empresa

  useEffect(() => {
    const count = products.filter(p => p.status === 'pending').length;
    setPendingCount(count);
  }, [products]);
  
  // Carregar categorias quando os produtos mudarem
  useEffect(() => {
    if (products.length > 0) {
      loadCategories();
    }
  }, [products]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedCategory) {
      params.set('category', selectedCategory);
    } else {
      params.delete('category');
    }
    params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [selectedCategory, currentPage]);

  useEffect(() => {
    const category = searchParams.get('category') || '';
    const page = parseInt(searchParams.get('page') || '1');
    setSelectedCategory(category);
    setCurrentPage(page);
  }, [searchParams]);

  // Função loadProducts removida pois agora usamos onSnapshot

  const loadCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const snapshot = await getDocs(categoriesRef);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        type: doc.data().type || 'normal'
      }));
      
      // Armazenar todas as categorias
      setAllCategories(categoriesData);
      
      // Identificar categorias que tu00eam produtos associados
      const categoriesWithProducts = new Set<string>();
      products.forEach(product => {
        if (product.category) {
          categoriesWithProducts.add(product.category);
        }
      });
      
      // Filtrar apenas categorias com produtos
      const filteredCategories = categoriesData.filter(category => 
        categoriesWithProducts.has(category.id)
      );
      
      console.log('Categorias com produtos:', filteredCategories.length, 'de', categoriesData.length);
      
      // Atualizar o estado das categorias
      setCategories(filteredCategories);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  const deleteProduct = async (product: Product) => {
    try {
      const collectionName = product.status === 'pending' ? 'pendingProducts' : 'products';
      await deleteDoc(doc(db, collectionName, product.id));
      setProducts(products.filter(p => p.id !== product.id));
      toast.success('Produto removido com sucesso!');
    } catch (err) {
      console.error('Erro ao remover produto:', err);
      toast.error('Erro ao remover produto');
    }
  };

  const handleDeleteSelectedProducts = async () => {
    try {
      await Promise.all(
        selectedProducts.map(async (productId) => {
          const product = products.find(p => p.id === productId);
          if (product) {
            const collectionName = product.status === 'pending' ? 'pendingProducts' : 'products';
            await deleteDoc(doc(db, collectionName, productId));
          }
        })
      );
      
      setProducts(products.filter(p => !selectedProducts.includes(p.id)));
      setSelectedProducts([]);
      toast.success('Produtos removidos com sucesso!');
    } catch (err) {
      console.error('Erro ao remover produtos:', err);
      toast.error('Erro ao remover produtos');
    }
    setShowDeleteSelectedModal(false);
  };

  const handleDeleteProduct = async (product: Product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return;

    await deleteProduct(selectedProduct);
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  const toggleSelectProduct = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const getStockStatusColor = (status: string, quantity: number) => {
    if (status === 'outofstock' || quantity === 0) return 'text-red-600';
    if (status === 'onbackorder') return 'text-orange-600';
    if (quantity <= 5) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getActivePrice = (product: Product) => {
    if (product.promotionalPeriod && product.salePrice) {
      const now = new Date();
      const startDate = new Date(product.promotionalPeriod.startDate);
      const endDate = new Date(product.promotionalPeriod.endDate);
      
      if (now >= startDate && now <= endDate) {
        return product.salePrice;
      }
    }
    return product.regularPrice;
  };

  const getCategoryName = (categoryId: string | null | undefined) => {
    if (!categoryId) return 'Sem categoria';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Categoria não encontrada';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = (
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.ean?.includes(searchTerm) ||
      product.baselinker_id?.includes(searchTerm)
    );

    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesPendingStatus = showPendingProducts ? product.status === 'pending' : product.status !== 'pending';

    return matchesSearch && matchesCategory && matchesPendingStatus;
  });

  // Calculate pagination
  const itemsPerPage = 25; // Definindo explicitamente o número de itens por página
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset para primeira página ao mudar categoria
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Produtos</h1>
            <div className="flex items-center gap-2">
              {selectedProducts.length > 0 && (
                <>
                  <button
                    onClick={() => setShowBulkUpdateModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Tag className="mr-2 h-4 w-4" />
                    Atualizar Categoria ({selectedProducts.length})
                  </button>
                  <button
                    onClick={() => setShowDeleteSelectedModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Apagar Selecionados ({selectedProducts.length})
                  </button>
                </>
              )}
              <ImportFromStoreButton onImport={() => setShowImportModal(true)} />
              <button
                onClick={() => setShowCategoryModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Tag className="mr-2 h-4 w-4" />
                {currentUser?.role === 'admin' ? 'Configurar Categorias' : 'Ver Categorias'}
              </button>
              <button
                onClick={() => setShowPendingProducts(!showPendingProducts)}
                className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  showPendingProducts 
                    ? 'border-primary text-primary bg-white hover:bg-gray-50'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                <Clock className="mr-2 h-4 w-4" />
                {showPendingProducts ? 'Ver Aprovados' : 'Ver Pendentes'}
                {!showPendingProducts && pendingCount > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                    {pendingCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/dashboard/products/create')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Plus className="mr-2 h-4 w-4" />
                Novo Produto
              </button>
            </div>
          </div>

          {products.length > 0 && (
            <div className="mb-6">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Pesquisar por nome, SKU, EAN ou ID Baselinker..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          {products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum produto encontrado</h3>
              <p className="text-gray-500">
                Comece adicionando seu primeiro produto clicando no botão "Criar Produto".
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th scope="col" className="w-12 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === products.length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th scope="col" className="w-16 px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Img
                    </th>
                    <th scope="col" className="w-1/3 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th scope="col" className="w-32 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      EAN
                    </th>
                    <th scope="col" className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th scope="col" className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço
                    </th>
                    {/* Coluna Categoria removida */}
                    <th scope="col" className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pricebench
                    </th>
                    <th scope="col" className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Última Atualização
                    </th>
                    <th scope="col" className="w-24 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentProducts.map((product) => (
                    <tr key={product.id} className={product.status === 'pending' ? 'bg-yellow-50' : ''}>
                      <td className="w-12 px-2 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => toggleSelectProduct(product.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="w-16 px-2 py-3 whitespace-nowrap">
                        {product.mainImage ? (
                          <img src={product.mainImage} alt={product.name} className="h-8 w-8 object-cover rounded" />
                        ) : (
                          <Package className="h-8 w-8 text-gray-400" />
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 truncate">{product.name}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{product.ean}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getStockStatusColor(product.stockStatus, product.stockQuantity)}`}>
                          {product.stockQuantity}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getActivePrice(product) === product.salePrice ? (
                            <>
                              <span className="text-red-600">€{getActivePrice(product).toFixed(2)}</span>
                              <span className="text-xs text-gray-500 line-through ml-1">€{product.regularPrice.toFixed(2)}</span>
                            </>
                          ) : (
                            `€${getActivePrice(product).toFixed(2)}`
                          )}
                        </div>
                        {product.isProPlus && (
                          <div className="text-xs text-blue-600">€{product.proPlusPrice.toFixed(2)}</div>
                        )}
                      </td>
                      {/* Cu00e9lula da coluna Categoria removida */}
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                        {product.inPricebench ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-gray-400" />}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                        {product.lastUpdate ? new Date(product.lastUpdate).toLocaleString('pt-PT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                        {product.status === 'approved' ? (
                          <>
                            <button
                              onClick={() => navigate(`/dashboard/products/${product.id}`)}
                              className="text-primary hover:text-primary-dark"
                              title="Ver detalhes"
                            >
                              <Eye size={20} />
                            </button>
                            <button
                              onClick={() => navigate(`/dashboard/products/${product.id}/edit`)}
                              className="text-primary hover:text-primary-dark"
                              title="Editar produto"
                            >
                              <PenSquare size={20} />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product)}
                              className="text-red-600 hover:text-red-700"
                              title="Apagar produto"
                            >
                              <Trash2 size={20} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="text-red-600 hover:text-red-700"
                            title="Apagar produto"
                          >
                            <Trash2 size={20} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col items-center gap-3 mt-4 pb-4">
                  <div className="text-sm text-gray-700">
                    <span>Mostrando </span>
                    <span className="font-medium">{indexOfFirstProduct + 1}</span>
                    <span> a </span>
                    <span className="font-medium">{Math.min(indexOfLastProduct, filteredProducts.length)}</span>
                    <span> de </span>
                    <span className="font-medium">{filteredProducts.length}</span>
                    <span> produtos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      «
                    </button>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      ‹
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(number => {
                          if (totalPages <= 7) return true;
                          if (number === 1 || number === totalPages) return true;
                          if (number >= currentPage - 2 && number <= currentPage + 2) return true;
                          return false;
                        })
                        .map((number, index, array) => (
                          <React.Fragment key={number}>
                            {index > 0 && array[index - 1] !== number - 1 && (
                              <span className="px-2 py-1 text-gray-500">...</span>
                            )}
                            <button
                              onClick={() => paginate(number)}
                              className={`px-3 py-1 rounded-md min-w-[2.25rem] ${
                                currentPage === number
                                  ? 'bg-primary text-white font-medium'
                                  : 'bg-white hover:bg-gray-50 text-gray-500 border'
                              }`}
                            >
                              {number}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      ›
                    </button>
                    <button
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-500 hover:bg-gray-50 border'
                      }`}
                    >
                      »
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <CategoryModal 
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
      />

      {showImportModal && (
        <WooCommerceProductImport
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadProducts();
            toast.success('Produtos importados com sucesso!');
          }}
        />
      )}

      <DeleteProductModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedProduct(null);
        }}
        onConfirm={handleDeleteConfirm}
        product={selectedProduct}
      />

      <DeleteProductModal
        isOpen={showDeleteSelectedModal}
        onClose={() => setShowDeleteSelectedModal(false)}
        onConfirm={handleDeleteSelectedProducts}
        title={`Apagar ${selectedProducts.length} produtos`}
        message="Tem certeza que deseja apagar todos os produtos selecionados? Esta ação não pode ser desfeita."
        selectedProducts={selectedProducts}
      />

      <BulkUpdateModal
        isOpen={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        selectedProducts={selectedProducts}
        onUpdate={() => {
          loadProducts();
          setSelectedProducts([]);
        }}
      />
    </div>
  );
}